// Reported choices only. Discovery describes an offer; Session.settings is
// the real session's confirmed state. Neither a default alias nor a request
// proves a concrete model or effort.
import { engineOf } from './format'
import { modelGroups, newestModel } from './models'
import type { AgentChoice, AgentOption, ConfigChoice, RunnerAgent, SessionSettings } from '../api/types'

export interface Choice { id: string; label: string; disabled?: boolean }
export const concrete = (value: string | null | undefined): value is string => !!value?.trim() && value.trim().toLowerCase() !== 'default'
const label = (choice: AgentChoice) => concrete(choice.name) ? choice.name : choice.value
export const choices = (values: readonly AgentChoice[] = []): Choice[] => values.filter(choice => concrete(choice.value)).map(choice => ({id: choice.value, label: label(choice)}))
const option = (options: readonly AgentOption[] = [], category: string) => options.find(value => value.category === category)
export const effortOption = (options: readonly AgentOption[] = [], adapter?: string | null) => options.find(value => value.category === 'thought_level' || (adapter === 'vibe' && value.category === 'thinking'))
export const modelChoices = (settings: SessionSettings): Choice[] => choices(option(settings.options, 'model')?.choices)
export function startupModels(agent: RunnerAgent | null): Choice[] {
  const reported = choices(option(agent?.catalogue?.options, 'model')?.choices)
  return (agent?.models ?? []).filter(concrete).map(id => reported.find(choice => choice.id === id) ?? {id, label: concrete(agent?.catalogue?.model_details?.[id]?.name) ? agent!.catalogue!.model_details![id]!.name! : id})
}
const isOpus = (id: string) => /(^|[^a-z0-9])opus($|[^a-z0-9])/i.test(id)
/**
 * The model a new session opens with when no pick of this browser stands, always one of those offered: the agent's
 * saved default (Settings, Agents), else the model it reports as its default, else for Claude its `opus` alias (which
 * follows Claude Code's newest Opus, while its id carries no version to rank) or else its latest Opus, else the first
 * of the latest models. Today's app opened the same way (Poulpe-497, Poulpe-548).
 */
export function savedModel(agent: RunnerAgent | null, offered: readonly Choice[]): string {
  const among = (id: string | null | undefined) => offered.find(choice => choice.id === id)?.id
  return among(agent?.preferences?.model) ?? among(agent?.default_model)
    ?? (agent && engineOf(agent.agent) === 'claude' ? among('opus') ?? newestModel(offered.filter(choice => isOpus(choice.id)))?.id : undefined)
    ?? modelGroups(offered).latest[0]?.id ?? ''
}
export function startupEffort(agent: RunnerAgent | null, model: string): AgentOption | undefined {
  // Effort depends on the model: the probe reports each model's own, and an
  // older runner only its scratch session's current model's.
  if (!concrete(model)) return undefined
  const own = agent?.catalogue?.model_details?.[model]?.effort
  if (own) return effortOption([own], agent?.agent)
  return agent?.catalogue?.current_model === model ? effortOption(agent.catalogue.options, agent.agent) : undefined
}
export function modelEffort(option: AgentOption | undefined, current: ConfigChoice | null): ConfigChoice | null {
  // Discovery's current value is this model's default. Retain a person's
  // current effort only when no concrete default is reported and it is offered.
  const offered = choices(option?.choices)
  const value = offered.find(choice => choice.id === option?.current_value)?.id ?? offered.find(choice => choice.id === current?.value)?.id
  return option && value ? {id: option.id, value} : null
}
export function modeChoices(values: readonly AgentChoice[] = []): Choice[] {
  // Every mode the agent offers, as in its own app (Poulpe-323).
  return values.map(choice => ({id: choice.value, label: choice.name || choice.value}))
}
/**
 * The mode a new session opens with, always one the agent offers: the person's pick, else the mode they saved for the
 * agent (Settings, Agents), else the agent's declared default, Claude's Auto (Poulpe-731); else none, and the agent's own.
 */
export function startupMode(agent: RunnerAgent | null, chosen: string | null | undefined): string {
  const offered = agent?.catalogue?.modes ?? []
  return [chosen, agent?.preferences?.mode, agent?.catalogue?.default_mode].find(id => !!id && offered.some(mode => mode.value === id)) ?? ''
}
export function currentEffort(settings: SessionSettings, adapter?: string | null): ConfigChoice | null {
  const found = effortOption(settings.options, adapter)
  return found && concrete(found.current_value) ? {id: found.id, value: found.current_value} : null
}
export function choiceLabel(values: readonly Choice[], value: string | null | undefined): string | null {
  return concrete(value) ? values.find(choice => choice.id === value)?.label ?? value : null
}
